import classNames from "classnames";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useFormatter, useTranslations } from "next-intl";
import { Fragment, useMemo } from "react";

import { CartProductTileContext } from "~/components/cart/cart-product-tile";
import Button from "~/components/common/button";
import HighlightedText from "~/components/common/highlighted-text";
import myaccount from "~/components/common/myaccount.module.scss";
import OrderInfo from "~/components/common/order-info";
import OverlayGeneric from "~/components/common/overlays/overlay-generic";
import GuestTrackYourOrderCartProductTile from "~/components/myaccount/orders/guest-track-your-order-cart-product-tile";
import GuestTrackYourOrderDiscountLinesTotals from "~/components/myaccount/orders/guest-track-your-order-discount-lines-totals";
import { useAuthContext } from "~/contexts/auth";
import { useConfiguration } from "~/contexts/configuration";
import { useGuestTrackYourOrder } from "~/hooks/use-guest-track-your-order";
import appRoutes from "~/utils/app-routes";
import Constants from "~/utils/constants";
import crossCountryUtils from "~/utils/cross-country-utils";
import intlUtils from "~/utils/intl-utils";
import loyaltyUtils from "~/utils/loyalty-utils";
import orderUtils from "~/utils/order-utils";
import productUtils from "~/utils/product-utils";

import Icon from "../../common/icon";
import LinkWrapper from "../../common/link-wrapper";
import GuestCancelOrder from "./guest-cancel-order";
import styles from "./order-details.module.scss";
import Progressbar from "./progressbar";

export default function GuestOrderDetail() {
  const t = useTranslations();
  const router = useRouter();
  const formatter = useFormatter();
  const priceFormatter = intlUtils.usePriceFormatter();
  const { guestTrackingToken } = useAuthContext();
  const { configuration, organizationUnitId } = useConfiguration();
  const { data, error, isLoading } = useGuestTrackYourOrder();

  const { hasLoyaltyProgram, userSubscriptions } = data?.loyalty || {
    hasLoyaltyProgram: !1,
    userSubscriptions: [],
  };

  const isOrderEligibleForReturn: boolean = useMemo(() => {
    return data?.order?.DeliveryDate ? orderUtils.isOrderEligibleForReturn(data?.order.DeliveryDate) : false;
  }, [data?.order?.DeliveryDate]);

  const orderReturnMaxDate: Date | undefined = useMemo(() => {
    return data?.order?.DeliveryDate ? orderUtils.getOrderReturnMaxDate(data.order.DeliveryDate) : undefined;
  }, [data?.order?.DeliveryDate]);

  const orderReturnDaysFromNow: number | undefined = useMemo(() => {
    return data?.order?.DeliveryDate ? orderUtils.getOrderReturnDaysFromNow(data.order.DeliveryDate) : undefined;
  }, [data?.order?.DeliveryDate]);

  if (isLoading) {
    return (
      <div style={{ position: "relative", minHeight: "200px" }}>
        <OverlayGeneric />
      </div>
    );
  }

  if (!data || error) {
    return <p className={myaccount.infoMessage}>{t("generic.couldntGetOrderDetails")}</p>;
  }

  const currency = data.order?.CurrencyID;
  const showExplicitTax = configuration.PreferredPriceDisplayMode == 1 && configuration?.CurrencyID == currency;
  const discountLines = data.amounts?.Discounts?.PerDiscount;
  // const firstShippingMethod = data.products.find((line) => line.ShippingMethod && line.ShippingMethod.Name);
  const isUserSubscribedToLoyalty = loyaltyUtils.isSubscribedToLoyalty(userSubscriptions);
  const orderLoyaltyDeposits = data.order?.TotalLoyaltyProgramDeposits;

  if (!guestTrackingToken || !data.order) {
    return <Button loading={true} style={{ cursor: "default" }}></Button>;
  }

  // @ts-ignore
  return (
    <>
      <Head>
        <title>{t("generic.orderDetailNumber", { number: data?.order?.ID })}</title>
      </Head>
      <div className={styles.orderDetailsWrapper}>
        <div className={styles.centeredTitle}>
          <h2>
            <HighlightedText text={t("generic.yourorderdetails")} />
          </h2>
          <p>
            {t("generic.myaccount.orderdetails.order_title_date")}{" "}
            {formatter.dateTime(new Date(data.order.PlacementDate || data.order.CreationTime), { dateStyle: "full" })}
          </p>
        </div>
        <div className={myaccount.myaccountCard}>
          <div className={myaccount.myaccountCardHeader}>
            <h3>{t("generic.myaccount.orderdetails.order_details_title")}</h3>
          </div>
          <div className={myaccount.myaccountCardContent}>
            <ul className={styles.orderDetailsList}>
              <li>
                <Icon name={"cart"} aria-hidden />
                <div>
                  <p className={styles.orderDetailsItemTitle}>
                    {data.order.TotalItems! + data.cancelledLines.length}{" "}
                    {t("generic.myaccount.orderdetails.items_count")}
                  </p>
                  <p>{priceFormatter.currency(Math.abs(data.order.TotalAmountInTax) ?? 0, { currency })}</p>
                </div>
              </li>
              <li>
                <Icon
                  name={
                    !Constants.RETURNABLE_ORDER_TYPES.includes(data.order.OrderCustomType?.BackendID || "") ||
                    data.order.UserAgentApplication === "eva-pos-app"
                      ? "store-locator"
                      : data.order.OrderCustomType?.BackendID === Constants.ORDER_TYPE_RESERVE
                      ? "click&collect"
                      : "country"
                  }
                  aria-hidden
                />
                <div>
                  <p className={styles.orderDetailsItemTitle}>{t("generic.myaccount.orderdetails.purchase_type")}</p>
                  <p>{t(productUtils.getOrderType(data.order))}</p>
                </div>
              </li>
              <li>
                <Icon name={"square-check"} aria-hidden />
                <div>
                  <p className={styles.orderDetailsItemTitle}>{t("generic.myaccount.orderdetails.purchase_number")}</p>
                  <p>{data.order.ID}</p>
                </div>
              </li>
            </ul>
            <p className={styles.orderDetailsStatusTitle}>
              {t("generic.myaccount.orderdetails.status")}: <span>{t(productUtils.getOrderStatus(data.order))}</span>
            </p>
            {(Constants.RETURNABLE_ORDER_TYPES.includes(data.order.OrderCustomType?.BackendID || "") ||
              data.order.UserAgentApplication != "eva-pos-app") && (
              <>
                {data.order.TotalItems != 0 && <Progressbar status={productUtils.getOrderStatusCode(data.order)} />}
                {/* show order cancellation if available */}
                <GuestCancelOrder order={data.order} />
                {data.order.TotalItems != 0 && (
                  <div className={styles.orderDetailsStatusButtons}>
                    {data.order.HasDelivery && data.shipmentTracking?.TrackingLink && (
                      <LinkWrapper
                        lookAsButton="primary"
                        href={
                          data.shipmentTracking.TrackingLink +
                          (data.shipmentTracking.TrackingCode
                            ? `?trackingId=${data.shipmentTracking.TrackingCode}`
                            : "")
                        }
                        target="_blank"
                      >
                        {t("generic.track")}
                      </LinkWrapper>
                    )}
                    {data.order.IsInvoiced && data.invoiceBlob && (
                      <LinkWrapper
                        lookAsButton="secondary"
                        href={`${configuration["Urls:Assets"]}/blob/${data.invoiceBlob.BlobID}`}
                        target="_blank"
                        download
                      >
                        {t("generic.download_invoice")}
                      </LinkWrapper>
                    )}
                    {data.returnAvailable && data.order.IsShipped && orderReturnMaxDate && isOrderEligibleForReturn && (
                      <>
                        {`${data.order.OriginatingOrganizationUnitID}` === `${organizationUnitId}` ? (
                          <>
                            <p className={myaccount.infoMessage}>
                              {t("generic.returns.requestuntil", {
                                returnWindowDays: orderReturnDaysFromNow!,
                                maxReturnDate: formatter.dateTime(orderReturnMaxDate, {
                                  dateStyle: "medium",
                                }),
                              })}
                            </p>
                            <LinkWrapper
                              href={
                                process.env.NEXT_PUBLIC_RETURNS_ENABLED === "true" &&
                                configuration["App:Ecommerce:EnableReturns"] === "true"
                                  ? appRoutes.REQUEST_RETURN + `/${data?.order?.ID}?fromGuest=true`
                                  : appRoutes.CUSTOMER_HELP_CENTER + `/?question=${t("generic.helpcenterquestion")}`
                              }
                              lookAsButton="secondary"
                            >
                              {t("generic.returns.requestcta")}
                            </LinkWrapper>
                          </>
                        ) : data.originatingOrgCountryCode ? (
                          <p className={myaccount.infoMessage}>
                            {t("generic.returnFromAnotherCountry", {
                              countryCode: data.originatingOrgCountryCode,
                            })}
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {data.order.TotalItems != 0 &&
          hasLoyaltyProgram &&
          isUserSubscribedToLoyalty &&
          orderLoyaltyDeposits?.[0]?.Points != undefined && (
            <div className={classNames(myaccount.myaccountCard, styles.orderDetailsKisses)}>
              {data.amounts?.Paid?.Amount ? (
                <p>
                  {t.rich("generic.withThisOrderYouHaveGained", {
                    points: "" + orderLoyaltyDeposits?.[0]?.Points,
                    br: (chunks) => <br />,
                    strong: (chunks) => <span className={styles.strong}>{chunks}</span>,
                    kissesContainer: (chunks) => <span className={styles.highlighted}>{chunks}</span>,
                  })}
                </p>
              ) : (
                <p>
                  {t.rich("generic.withThisOrderYouWillGain", {
                    points: "",
                    br: (chunks) => <br />,
                    strong: (chunks) => <span className={styles.strong}>{chunks}</span>,
                    kissesContainer: (chunks) => <span className={styles.highlighted}>{chunks}</span>,
                    myAccountLink: (chunks) => (
                      <Link className={styles.link} href={appRoutes.MYACCOUNT}>
                        {chunks}
                      </Link>
                    ),
                  })}
                </p>
              )}
            </div>
          )}

        <div className={myaccount.myaccountCard}>
          <div className={myaccount.myaccountCardHeader}>
            <h3 className={styles.orderDetailsItemsListTitle}>
              {t("generic.myaccount.orderdetails.product_list_title", {
                productsLength: data.order.TotalItems! + data.cancelledLines.length,
              })}
            </h3>
          </div>
          <div className={myaccount.myaccountCardContent}>
            <div className={styles.orderItems}>
              {data.products?.map((line, idx) => {
                return (
                  <Fragment key={line.ID}>
                    <GuestTrackYourOrderCartProductTile
                      line={line}
                      disableCartActions={true}
                      giftCardData={data.giftcards?.find((card) => `${card.Identifiers?.OrderLineID}` === `${line.ID}`)}
                      physicalGiftCardRequirement={
                        data?.additionalOrderData?.ProductRequirements?.find((el) => el.OrderLineID == line.ID)
                          ?.RequirementModels
                      }
                      context={CartProductTileContext.orderHistoryDetail}
                      guestPrefiguredDiscounts={data.prefigureDiscounts}
                    />
                    <div className={styles.divider} aria-hidden />
                  </Fragment>
                );
              })}
              <div className={styles.orderResume}>
                <p>
                  <span>{t("generic.subtotal")}</span>
                  <span className={styles.orderResumeValue}>
                    {priceFormatter.currency(
                      Math.abs(
                        showExplicitTax
                          ? data.amounts?.Types?.NormalProduct.Amount!
                          : data.amounts?.Types?.NormalProduct.InTax!
                      ),
                      { currency }
                    )}
                  </span>
                </p>
                <GuestTrackYourOrderDiscountLinesTotals
                  orderId={data.order.ID}
                  lines={discountLines || []}
                  prefiguredDiscounts={data.prefigureDiscounts}
                  currency={currency!}
                />
                {(data.amounts?.Types?.GiftWrappingCosts?.Amount! > 0 ||
                  data.amounts?.Types?.GiftWrappingCosts?.InTax! > 0) && (
                  <p>
                    <span>{t("generic.giftWrappingCost")}</span>
                    <span className={styles.orderResumeValue}>
                      {priceFormatter.currency(
                        showExplicitTax
                          ? Math.abs(data.amounts?.Types?.GiftWrappingCosts.Amount!)
                          : Math.abs(data.amounts?.Types?.GiftWrappingCosts.InTax!),
                        { currency }
                      )}
                    </span>
                  </p>
                )}
                {data.order.HasDelivery && data.order.ShippingAddress!.CountryID && (
                  <p>
                    <span>
                      {t("generic.shipping_to", {
                        destination: crossCountryUtils.getLocalizedCountryName(
                          data.order.ShippingAddress!.CountryID!,
                          router
                        ),
                        method:
                          data.order.Lines?.find((line) => line.ShippingMethod && !line.IsCancelled)?.ShippingMethod!
                            .Name || "",
                      })}
                    </span>
                    <span className={styles.orderResumeValue}>
                      {priceFormatter.currency(
                        (showExplicitTax
                          ? data.amounts?.Types?.ShippingCosts?.Amount
                          : data.amounts?.Types?.ShippingCosts?.InTax) || 0,
                        { currency }
                      )}
                    </span>
                  </p>
                )}

                {showExplicitTax && data.amounts?.Total?.InTax != 0 && (
                  <p>
                    <span>{t("generic.taxes")}</span>
                    <span className={styles.orderResumeValue}>
                      {priceFormatter.currency(Math.abs(data.amounts!.Total!.Tax!), {
                        currency,
                      })}
                    </span>
                  </p>
                )}
                <p className={styles.orderResumeTotal}>
                  <span>{t("generic.total")}</span>
                  <span>{priceFormatter.currency(Math.abs(data.order.TotalAmountInTax), { currency })}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <OrderInfo
          order={data.order}
          additionalOrderData={data.additionalOrderData}
          className={styles.generalOrderInfo}
          cardClassName={styles.orderInfoCard}
          cardHeaderClassName={styles.orderInfoCardHeader}
          cardContentClassName={myaccount.myaccountCardContent}
          amounts={data.amounts}
          digitalGiftcards={data.giftcards ?? []}
        />
      </div>
    </>
  );
}
